// #politics — hovering (or tapping, on touch) a historical figure's name
// reveals their portrait behind the text at a random spot in the viewport.
// Portrait data comes from the stage's data-figures JSON (template-homepage.php).

const MAX_VH = 0.75;      // portrait height cap, as a share of viewport height
const MAX_VW = 0.8;       // width cap for narrow (phone) viewports
const MARGIN = 16;        // keep portraits this far from the viewport edges (px)
const MIN_JUMP = 0.25;    // new spot must move at least this share of the viewport

export function initFigurePortraits() {
  const stage = document.querySelector('.hp-figure-stage');
  const section = stage?.closest('.hp-politics');
  if (!stage || !section) return;

  let figures;
  try {
    figures = JSON.parse(stage.dataset.figures || '{}');
  } catch {
    return;
  }

  const links = section.querySelectorAll('.hp-figure-link[data-figure]');
  if (!links.length) return;

  const masthead = document.getElementById('masthead');
  let activeLink = null;
  let currentImg = null;
  let lastSpot = null;
  let lastPointerType = 'mouse';
  let preloaded = false;

  function preload() {
    if (preloaded) return;
    preloaded = true;
    Object.values(figures).forEach(({ src }) => {
      new Image().src = src;
    });
  }

  // Size within the caps (never larger than the file itself), then pick a
  // random spot below the sticky header that differs from the previous one.
  function place(img, { w, h }) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const top0 = (masthead?.offsetHeight || 0) + MARGIN;

    let height = Math.min(vh * MAX_VH, h, vh - top0 - MARGIN);
    let width = height * (w / h);
    if (width > vw * MAX_VW) {
      width = vw * MAX_VW;
      height = width * (h / w);
    }

    const rangeX = Math.max(0, vw - width - MARGIN * 2);
    const rangeY = Math.max(0, vh - top0 - height - MARGIN);

    let spot;
    for (let i = 0; i < 6; i++) {
      spot = { x: MARGIN + Math.random() * rangeX, y: top0 + Math.random() * rangeY };
      if (!lastSpot) break;
      const dx = Math.abs(spot.x - lastSpot.x) / vw;
      const dy = Math.abs(spot.y - lastSpot.y) / vh;
      if (Math.max(dx, dy) >= MIN_JUMP) break;
    }
    lastSpot = spot;

    Object.assign(img.style, {
      width: `${width}px`,
      height: `${height}px`,
      left: `${spot.x}px`,
      top: `${spot.y}px`,
    });
  }

  function hide() {
    if (currentImg) {
      const img = currentImg;
      img.classList.remove('is-visible');
      img.addEventListener('transitionend', () => img.remove(), { once: true });
      setTimeout(() => img.remove(), 1000); // fallback if no transition runs
      currentImg = null;
    }
    activeLink?.classList.remove('is-active');
    activeLink = null;
  }

  function show(link) {
    if (link === activeLink) return;
    const data = figures[link.dataset.figure];
    if (!data) return;

    hide();
    preload();

    const img = document.createElement('img');
    img.className = 'hp-figure-stage__img';
    img.src = data.src;
    img.alt = '';
    img.decoding = 'async';
    place(img, data);
    stage.append(img);

    // Next frame so the fade-in transition runs from the initial state
    requestAnimationFrame(() => {
      requestAnimationFrame(() => img.classList.add('is-visible'));
    });

    currentImg = img;
    activeLink = link;
    link.classList.add('is-active');
  }

  // Track the input type so a tap toggles while a mouse click doesn't
  document.addEventListener('pointerdown', (e) => {
    lastPointerType = e.pointerType;
  }, { passive: true });

  links.forEach((link) => {
    link.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse') show(link);
    });
    link.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'mouse' && link === activeLink) hide();
    });

    // Keyboard focus only — tapping can also focus the button
    link.addEventListener('focus', () => {
      if (link.matches(':focus-visible')) show(link);
    });
    link.addEventListener('blur', () => {
      if (link === activeLink && lastPointerType === 'mouse') hide();
    });

    link.addEventListener('click', () => {
      if (lastPointerType !== 'touch' && lastPointerType !== 'pen') return;
      if (link === activeLink) hide();
      else show(link);
    });
  });

  // Touch: tapping anywhere else dismisses the portrait
  document.addEventListener('click', (e) => {
    if (activeLink && !e.target.closest('.hp-figure-link')) hide();
  });

  // Dismiss once the active name scrolls out of view (mainly for touch)
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting && entry.target === activeLink) hide();
    });
  });
  links.forEach((link) => observer.observe(link));

  // Width changes only — mobile URL-bar show/hide fires height-only resizes
  let lastWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    hide();
  }, { passive: true });
}
